import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  User, Target, Calendar, Heart, Sparkles, TrendingUp,
  Clock, MessageSquare, Award, Star, CheckCircle2, ArrowRight,
  Zap, Brain, Compass, ClipboardList
} from 'lucide-react';
import { Button } from './ui/button';
import { useAudience } from '../context/AudienceContext';
import FiveDAssessment from './FiveDAssessment';

const B2CValueSection = ({ onShopClick, onCoachesClick }) => {
  const { t } = useTranslation();
  const { isB2C, hasSelected } = useAudience();
  const [showAssessment, setShowAssessment] = useState(false);

  // Only show if B2C is selected or no selection made
  if (hasSelected && !isB2C) return null;

  const handleAssessmentComplete = (results) => {
    // After assessment, navigate to coaches/shop
    if (onShopClick) {
      onShopClick('individual');
    }
  };

  const benefits = [
    {
      icon: Target,
      title: t('b2cValue.benefits.goals.title', 'Achieve Your Goals'),
      description: t('b2cValue.benefits.goals.description', 'Work with your coach to set clear, achievable goals and create actionable plans to reach them.'),
      color: 'emerald'
    },
    {
      icon: Brain,
      title: t('b2cValue.benefits.clarity.title', 'Gain Clarity'),
      description: t('b2cValue.benefits.clarity.description', 'Discover your strengths, overcome limiting beliefs, and find direction in your career and life.'),
      color: 'teal'
    },
    {
      icon: Zap,
      title: t('b2cValue.benefits.confidence.title', 'Build Confidence'),
      description: t('b2cValue.benefits.confidence.description', 'Develop unshakeable self-confidence and the skills to tackle any challenge that comes your way.'),
      color: 'cyan'
    },
    {
      icon: TrendingUp,
      title: t('b2cValue.benefits.growth.title', 'Accelerate Growth'),
      description: t('b2cValue.benefits.growth.description', 'Fast-track your personal and professional development with expert guidance and accountability.'),
      color: 'green'
    },
    {
      icon: Heart,
      title: t('b2cValue.benefits.balance.title', 'Find Balance'),
      description: t('b2cValue.benefits.balance.description', 'Create harmony between work and life while maintaining peak performance in both areas.'),
      color: 'emerald'
    },
    {
      icon: Compass,
      title: t('b2cValue.benefits.purpose.title', 'Discover Purpose'),
      description: t('b2cValue.benefits.purpose.description', 'Uncover your true calling and align your daily actions with your deeper sense of purpose.'),
      color: 'teal'
    }
  ];

  const steps = [
    {
      number: '01',
      title: t('b2cValue.steps.step1.title', 'Take the Assessment'),
      description: t('b2cValue.steps.step1.description', 'Complete our quick assessment to help us understand your goals and preferences.')
    },
    {
      number: '02',
      title: t('b2cValue.steps.step2.title', 'Get Matched'),
      description: t('b2cValue.steps.step2.description', 'Our AI matches you with the perfect certified coach based on your unique needs.')
    },
    {
      number: '03',
      title: t('b2cValue.steps.step3.title', 'Start Growing'),
      description: t('b2cValue.steps.step3.description', 'Begin your coaching journey with flexible scheduling that fits your lifestyle.')
    }
  ];

  const testimonial = {
    quote: t('b2cValue.testimonial.quote', "Working with my CoachMeUpp coach transformed my career. I went from feeling stuck to landing my dream promotion in just 3 months."),
    author: t('b2cValue.testimonial.author', 'Sarah K.'),
    role: t('b2cValue.testimonial.role', 'Marketing Director'),
    rating: 5
  };

  const colorClasses = {
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
    teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200' },
    cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200' },
    green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
  };

  return (
    <section className="py-20 bg-gradient-to-b from-emerald-50 to-white" id="b2c-value">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <User className="w-4 h-4" />
            {t('b2cValue.badge', 'Personal Coaching')}
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t('b2cValue.title', 'Your Journey to Success')}
          </h2>
          <p className="text-lg text-gray-600">
            {t('b2cValue.subtitle', 'Personalized 1:1 coaching designed to help you achieve your personal and professional goals.')}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-16">
          {benefits.map((benefit, idx) => {
            const Icon = benefit.icon;
            const colors = colorClasses[benefit.color];
            return (
              <div key={benefit.title || `b2c-benefit-${idx}`} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all hover:-translate-y-1">
                <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 ${colors.text}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            );
          })}
        </div>

        {/* How It Works */}
        <div className="max-w-4xl mx-auto mb-16">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">
            {t('b2cValue.howItWorks', 'How It Works')}
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
              <div key={`b2c-step-${idx}`} className="text-center relative">
                <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <span className="text-2xl font-bold text-white">{step.number}</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{step.title}</h4>
                <p className="text-sm text-gray-600">{step.description}</p>
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-emerald-200" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-lg border border-emerald-100 relative">
            <div className="absolute -top-4 left-10">
              <div className="bg-emerald-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-2xl font-serif">
                "
              </div>
            </div>
            <div className="flex mb-4">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={`star-${i}`} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <p className="text-lg text-gray-700 italic mb-6">"{testimonial.quote}"</p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <div className="font-bold text-gray-900">{testimonial.author}</div>
                <div className="text-sm text-gray-600">{testimonial.role}</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="text-center space-y-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg font-semibold shadow-lg rounded-xl"
              onClick={() => setShowAssessment(true)}
              data-testid="b2c-start-transformation-btn"
            >
              <ClipboardList className="mr-2 h-5 w-5" />
              {t('b2cValue.cta.startTransformation', 'Start Free 5D Assessment')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-lg font-semibold rounded-xl"
              onClick={() => onShopClick?.('individual')}
              data-testid="b2c-packages-btn"
            >
              <Calendar className="mr-2 h-5 w-5" />
              {t('b2cValue.cta.packages', 'View Packages')}
            </Button>
          </div>
          <button 
            onClick={onCoachesClick}
            className="text-emerald-600 hover:text-emerald-700 font-medium text-sm inline-flex items-center gap-1"
          >
            {t('b2cValue.cta.meetCoaches', 'Meet Our Coaches')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* 5D Assessment Modal */}
        <FiveDAssessment 
          isOpen={showAssessment}
          onClose={() => setShowAssessment(false)}
          onComplete={handleAssessmentComplete}
        />
      </div>
    </section>
  );
};

export default B2CValueSection;
